// Cloud's routes outside /me, put next to Me's by createMeApp's `routes`.
//
// - /signin, /signin/done (back from Google), /waitlist, /passkey: getting in.
// - /oauth/consent: an MCP client or the desktop app asks to act for the person.
// - /hub/login (`?code=`): a Hub (the offline edition) asks to be tied to the
//   account with the code it shows.
// - /hub/pair: where a Hub's pairing QR points (the app opens instead on a
//   phone that has it); open without an account.
// - /trust/approve: where a device's QR to be added to the trusted devices
//   points (the same: the app opens instead).
// - /settings/security, /settings/apps: kept as addresses; the items live in
//   Me's settings (/me/settings/<id>), one list for every setting.
// The Workspace's routes (/<slug>/..., /new, /invite/<token>) are in
// workspace/routes.tsx, the operator's place (/admin/...) in admin/admin.tsx.
// Pages that need an account check it in beforeLoad (no session: /signin with
// `next`; waitlisted: /waitlist). In development with auto sign-in, a missing
// session signs in once instead.
import type { QueryClient } from "@tanstack/react-query";
import { type AnyRoute, createRoute, redirect } from "@tanstack/react-router";
import { type Api, ApiError } from "@trameria/ui-core";
import { signInDestination } from "./auth/destination";
import { HOME, parseNextSearch } from "./auth/next";
import { desktopFrame, tellSignedOut } from "./desktop-frame";
import { type Account, accountQuery, authConfigQuery } from "./queries";
import { ConsentPage } from "./screens/consent";
import { HubLoginPage } from "./screens/hub-login";
import { HubPairPage } from "./screens/hub-pair";
import { PasskeyPage } from "./screens/passkey";
import { SignInPage } from "./screens/signin";
import { SignInInAppPage } from "./screens/signin-in-app";
import { TrustApprovePage } from "./screens/trust-approve";
import { WaitlistPage } from "./screens/waitlist";
import type { CloudSession } from "./session";

export interface Ctx {
  queryClient: QueryClient;
  api: Api;
}

const unauthorized = (error: unknown) =>
  error instanceof ApiError && error.status === 401;

/** The signed-in account, signing in once in development; null when signed out. */
export async function currentAccount(
  ctx: Ctx,
  session: CloudSession,
): Promise<Account | null> {
  const fetchAccount = () =>
    ctx.queryClient.fetchQuery({ ...accountQuery(ctx.api), staleTime: 0 });
  try {
    return await fetchAccount();
  } catch (error) {
    if (!unauthorized(error)) throw error;
  }
  if (!(await session.devSignIn())) return null;
  return fetchAccount();
}

/** For pages that need an approved account. */
export async function requireApproved(
  ctx: Ctx,
  session: CloudSession,
  back: string,
): Promise<Account> {
  const account = await currentAccount(ctx, session);
  if (!account) throw redirect({ to: "/signin", search: { next: back } });
  if (!account.approved) throw redirect({ to: "/waitlist" });
  return account;
}

export function cloudRoutes(root: AnyRoute, session: CloudSession): AnyRoute[] {
  const signin = createRoute({
    getParentRoute: () => root,
    path: "signin",
    validateSearch: parseNextSearch,
    // Inside the desktop app no sign-in is drawn: its session is the app's,
    // made from the app's own sign-in (a device code approved in the
    // browser). A passkey or Google here never comes back to the app. Signed
    // in again (the app made the session and loaded this page anew): on to
    // where it was asked from; else the app is told. Told already as often
    // as it is (the app could not make it again: an app before this one goes
    // on loading the page), the way back is drawn instead.
    beforeLoad: async ({ context, search }) => {
      if (!desktopFrame) return { signInInApp: false };
      const account = await currentAccount(context as Ctx, session);
      if (account) {
        const next = (search as { next?: string }).next;
        throw redirect({
          href: account.approved ? (next ?? HOME) : "/waitlist",
        });
      }
      return { signInInApp: !tellSignedOut() };
    },
    // Which buttons to show is known before drawing (no Google button popping
    // in late). A failure still shows the email form.
    loader: ({ context }) => {
      if (desktopFrame) return;
      const ctx = context as unknown as Ctx;
      return ctx.queryClient
        .ensureQueryData(authConfigQuery(ctx.api))
        .catch(() => undefined);
    },
    component: function SignInRoute() {
      const { next } = signin.useSearch() as { next?: string };
      const { signInInApp } = signin.useRouteContext() as {
        signInInApp: boolean;
      };
      if (signInInApp) return <SignInInAppPage />;
      if (desktopFrame) return <div className="h-full bg-surface" />;
      return <SignInPage next={next} />;
    },
  });

  const signinDone = createRoute({
    getParentRoute: () => root,
    path: "signin/done",
    validateSearch: parseNextSearch,
    beforeLoad: async ({ context, search }) => {
      const ctx = context as Ctx;
      const next = (search as { next?: string }).next;
      let href: string;
      try {
        href = await signInDestination(ctx.queryClient, ctx.api, next);
      } catch {
        // Back from Google without a session: try again from the start.
        throw redirect({ to: "/signin", search: next ? { next } : {} });
      }
      await session.start();
      throw redirect({ href });
    },
  });

  const waitlist = createRoute({
    getParentRoute: () => root,
    path: "waitlist",
    beforeLoad: async ({ context }) => {
      const account = await currentAccount(context as Ctx, session);
      if (!account) throw redirect({ to: "/signin" });
      if (account.approved) throw redirect({ to: HOME });
    },
    component: WaitlistPage,
  });

  const passkey = createRoute({
    getParentRoute: () => root,
    path: "passkey",
    validateSearch: parseNextSearch,
    beforeLoad: ({ context, location }) =>
      requireApproved(context as Ctx, session, location.href),
    component: function PasskeyRoute() {
      const { next } = passkey.useSearch() as { next?: string };
      return <PasskeyPage next={next} />;
    },
  });

  const consent = createRoute({
    getParentRoute: () => root,
    path: "oauth/consent",
    beforeLoad: ({ context, location }) =>
      requireApproved(context as Ctx, session, location.href),
    component: ConsentPage,
  });

  const hubLogin = createRoute({
    getParentRoute: () => root,
    path: "hub/login",
    validateSearch: (search: Record<string, unknown>) =>
      typeof search.code === "string" ? { code: search.code } : {},
    beforeLoad: ({ context, location }) =>
      requireApproved(context as Ctx, session, location.href),
    component: function HubLoginRoute() {
      const { code } = hubLogin.useSearch() as { code?: string };
      return <HubLoginPage code={code} />;
    },
  });

  // Open without an account: a phone without the app lands here from a Hub's QR.
  const hubPair = createRoute({
    getParentRoute: () => root,
    path: "hub/pair",
    component: HubPairPage,
  });

  // The same for a device's QR to be added to the trusted devices.
  const trustApprove = createRoute({
    getParentRoute: () => root,
    path: "trust/approve",
    component: TrustApprovePage,
  });

  const settingsAlias = (item: string) =>
    createRoute({
      getParentRoute: () => root,
      path: `settings/${item}`,
      beforeLoad: () => {
        throw redirect({ to: `/me/settings/${item}`, replace: true });
      },
    });

  return [
    signin,
    signinDone,
    waitlist,
    passkey,
    consent,
    hubLogin,
    hubPair,
    trustApprove,
    settingsAlias("security"),
    settingsAlias("apps"),
  ];
}
